import './styles.scss';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {
  Card,
  Grid,
  MenuItem,
  Select,
  Checkbox,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useForm, useWatch, useFieldArray, Controller} from 'react-hook-form';
import {Axios} from 'services/axios';
import Notiflix, {Notify, Loading} from 'notiflix';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DataTable from 'examples/Tables/DataTable';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import AdditionalFeatureAssignment from './AdditionalFeatureAssignment';

import {useDuration} from 'context/DurationContext';
import {useCountry} from 'context/CountryContext';

const SUBSCRIPTION_PLAN_TYPES = [
  {key: 'one_time', label: 'One Time'},
  {key: 'subscription_cancellation_allowed', label: 'With Cancellation'},
  {key: 'subscription_no_cancellation', label: 'Without Cancellation'},
];

export default function ProductFeatureAssignment() {
  const {productId} = useParams();
  const [params] = useSearchParams();
  const categoryId = params.get('categoryId');

  const {
    durations,
    loading: durationLoading,
    error: durationError,
    getDefaultDuration,
  } = useDuration();

  const {
    countries,
    loading: countryLoading,
    error: countryError,
    getDefaultCountry,
  } = useCountry();

  const [features, setFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [selectedAdditionalFeatures, setSelectedAdditionalFeatures] = useState(
    [],
  );
  const [
    customSelectedAdditionalFeatures,
    setCustomSelectedAdditionalFeatures,
  ] = useState([]);
  const [customPrices, setCustomPrices] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recentlyAdded');
  const [isFeatureTableExpanded, setIsFeatureTableExpanded] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [
    initialSelectedAdditionalFeatureIds,
    setInitialSelectedAdditionalFeatureIds,
  ] = useState([]);

  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryPricing, setCountryPricing] = useState({});
  const [selectedCountryData, setSelectedCountryData] = useState([{}, {}, {}]);
  const [isCreatePricingOpen, setIsCreatePricingOpen] = useState(false);
  const [activeDurationTab, setActiveDurationTab] = useState(null);
  const [activeCountryFilter, setActiveCountryFilter] = useState({});

  const [fetchedPackages, setFetchedPackages] = useState(null);
  const [globalFeatures, setGlobalFeatures] = useState({
    Low: [],
    Mid: [],
    High: [],
  });
  const [editingPricing, setEditingPricing] = useState({});
  const [editValues, setEditValues] = useState({});

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: {errors},
  } = useForm({
    defaultValues: {
      packages: [
        {
          packageType: 'Low',
          packageName: '',
          packageDescription: '',
          low_pkg_image_url: null,
          badges: [],
        },
        {
          packageType: 'Mid',
          packageName: '',
          packageDescription: '',
          mid_pkg_image_url: null,
          badges: [],
        },
        {
          packageType: 'High',
          packageName: '',
          packageDescription: '',
          high_pkg_image_url: null,
          badges: [],
        },
      ],
    },
  });

  const watchedPackages = useWatch({control, name: 'packages'});

  const badgesFieldArray0 = useFieldArray({control, name: 'packages.0.badges'});
  const badgesFieldArray1 = useFieldArray({control, name: 'packages.1.badges'});
  const badgesFieldArray2 = useFieldArray({control, name: 'packages.2.badges'});
  const badgesFieldArrays = [
    badgesFieldArray0,
    badgesFieldArray1,
    badgesFieldArray2,
  ];

  const watchedDurations = useWatch({control, name: 'durations'});

  const formatDurationLabel = duration => {
    if (!duration) return '';
    if (duration.displayLabel) return duration.displayLabel;
    if (duration.label) return duration.label;
    const value = duration.value || 0;
    const unit = duration.unit || 'month';
    const unitLabels = {
      day: 'Day',
      week: 'Week',
      month: 'Month',
      quarter: 'Quarter',
      biannual: '6 Months',
      year: 'Year',
      custom: 'Custom',
    };
    const unitLabel = unitLabels[unit] || unit;
    const plural = value > 1 ? 's' : '';
    return `${value} ${unitLabel}${plural}`;
  };

  const getCountryLabel = country => {
    if (!country) return '';
    return `${country.name} (${country.countryCode})`;
  };

  useEffect(() => {
    if (durations.length > 0 && !selectedDuration) {
      const defaultDuration = getDefaultDuration();
      if (defaultDuration) setSelectedDuration(defaultDuration.id);
    }
  }, [durations, selectedDuration]);

  useEffect(() => {
    if (countries.length > 0 && !selectedCountry) {
      const defaultCountry = getDefaultCountry();
      if (defaultCountry) setSelectedCountry(defaultCountry.id);
    }
  }, [countries, selectedCountry]);

  const handleCustomPriceChange = useCallback((featureId, value) => {
    setCustomPrices(prev => ({...prev, [featureId]: value}));
  }, []);

  const handleCheckboxChange = useCallback((featureId, pkgType) => {
    setSelectedFeatures(prev => {
      const current = prev[featureId] || {};
      return {...prev, [featureId]: {...current, [pkgType]: !current[pkgType]}};
    });
  }, []);

  const memoizedSetSelectedAdditionalFeatures = useCallback(
    features => setSelectedAdditionalFeatures(features),
    [],
  );

  const getPackagesByProductId = useCallback(async () => {
    try {
      const response = await Axios.fetch(
        `/shop/products/${productId}/packages?allCountries=true`,
      );
      return response.data.data;
    } catch (error) {
      Notify.failure(
        error.response?.data?.message || 'Failed to fetch packages',
      );
      console.error('Error fetching packages:', error);
    }
  }, [productId]);

  useEffect(() => {
    async function fetchAndFillPackages() {
      const apiData = await getPackagesByProductId();
      if (!apiData) return;
      setFetchedPackages(apiData);

      const pkgKeys = ['low', 'mid', 'high'];
      const packageTypeLabels = ['Low', 'Mid', 'High'];

      const globalFeaturesData = {Low: [], Mid: [], High: []};
      pkgKeys.forEach((pkgKey, idx) => {
        const pkgItem = apiData.packages?.[pkgKey];
        if (pkgItem && pkgItem.features) {
          globalFeaturesData[packageTypeLabels[idx]] = pkgItem.features;
        }
      });
      setGlobalFeatures(globalFeaturesData);

      const additionalFeaturesList =
        apiData.packages?.low?.additionalFeatures ||
        apiData.packages?.mid?.additionalFeatures ||
        apiData.packages?.high?.additionalFeatures ||
        [];
      if (
        Array.isArray(additionalFeaturesList) &&
        additionalFeaturesList.length
      ) {
        const allAdditionalFeatureIds = additionalFeaturesList.map(
          feature => feature.additionalFeatureId,
        );
        setInitialSelectedAdditionalFeatureIds(allAdditionalFeatureIds);
        setCustomSelectedAdditionalFeatures(additionalFeaturesList);
      }
      const builtCountryPricing = buildCountryPricingFromApi(apiData);
      setCountryPricing(builtCountryPricing);
      const firstDuration = Object.keys(builtCountryPricing)[0];
      if (firstDuration) setActiveDurationTab(firstDuration);

      const packagesArray = pkgKeys.map((pkgKey, idx) => {
        const pkgData = apiData.packages?.[pkgKey];
        if (!pkgData) return {packageType: packageTypeLabels[idx], badges: []};
        return {
          packageId: pkgData.id,
          packageType: pkgData.type,
          packageName: pkgData.name,
          packageDescription: pkgData.description,
          [pkgData.type.toLowerCase() + '_pkg_image_url']: pkgData.image_url,
          finalPackagePriceMonthly: pkgData.finalMonthlyPrice,
          finalMonthlyPrice: pkgData.finalMonthlyPrice,
          tentativeMonthlyPrice: pkgData.tentativeMonthlyPrice,
          badges: Array.isArray(pkgData.badges) ? pkgData.badges : [],
        };
      });
      reset({packages: packagesArray, durations: []});
    }
    fetchAndFillPackages();
  }, [getPackagesByProductId, reset]);

  const buildCountryPricingFromApi = apiData => {
    const pkgKeys = ['low', 'mid', 'high'];
    const packageTypeLabels = ['Low', 'Mid', 'High'];
    const countryPricing = {};

    pkgKeys.forEach((pkgKey, pkgIndex) => {
      const pkg = apiData.packages?.[pkgKey];
      if (!pkg || !Array.isArray(pkg.durations)) return;
      const packageType = pkg.type || packageTypeLabels[pkgIndex];

      pkg.durations.forEach(durationEntry => {
        const duration = durationEntry.value;
        const country = durationEntry.country;
        const pricing = durationEntry.pricing;
        if (duration == null || !country || !pricing) return;

        if (!countryPricing[duration])
          countryPricing[duration] = {defaultPricing: {}, countryPricing: {}};

        const priceEntry = {
          packageType,
          finalPrice: pricing.finalPrice,
          price: pricing.finalPrice,
          priceBeforeDiscount: pricing.priceBeforeDiscount,
          discountType: pricing.discount?.type || '',
          discountValue: pricing.discount?.value || '',
          subscriptionOptions: pricing.subscriptionOptions || {},
        };

        // Every country gets its own row with its real pricing — even a
        // country with no explicit override still has a real (fallback)
        // price and must be shown as itself, not folded away.
        if (!countryPricing[duration].countryPricing[country.code]) {
          countryPricing[duration].countryPricing[country.code] = {};
        }
        countryPricing[duration].countryPricing[country.code][pkgIndex] =
          priceEntry;

        // Submit still needs one duration-level base price to send to the
        // API; keep the first fallback (non-override) row for that.
        if (
          !durationEntry.metadata?.isUserCountryPricing &&
          !countryPricing[duration].defaultPricing[pkgIndex]
        ) {
          countryPricing[duration].defaultPricing[pkgIndex] = priceEntry;
        }
      });
    });
    return countryPricing;
  };

  const handleDeleteCountryPricing = (duration, countryCode) => {
    setCountryPricing(prev => {
      const updated = {...prev};
      if (updated[duration]?.countryPricing?.[countryCode]) {
        const {[countryCode]: removed, ...restCountries} =
          updated[duration].countryPricing;
        updated[duration].countryPricing = restCountries;
      }
      return updated;
    });
  };

  useEffect(() => {
    if (categoryId) {
      async function fetchFeatures() {
        try {
          const response = await Axios.fetch(
            `shop/features/category/${categoryId}`,
          );
          const enhanced = response.data.data.map(feature => ({
            ...feature,
            featureKey: feature.id,
          }));
          setFeatures(enhanced);
        } catch (error) {
          Notify.failure(
            error.response?.data?.message || 'Failed to fetch features',
          );
          console.error('Error fetching features:', error);
        }
      }
      fetchFeatures();
    }
  }, [categoryId]);

  useEffect(() => {
    if (fetchedPackages && features.length) {
      const newSelected = {};
      ['low', 'mid', 'high'].forEach(pkgKey => {
        const pkgItem = fetchedPackages.packages?.[pkgKey];
        if (pkgItem?.features) {
          const pkgType = pkgItem.type;
          pkgItem.features.forEach(globalFeat => {
            const featureId = globalFeat.featureId;
            newSelected[featureId] = {
              ...(newSelected[featureId] || {}),
              [pkgType]: true,
            };
          });
        }
      });
      setSelectedFeatures(newSelected);
    }
  }, [fetchedPackages, features]);

  const displayedFeatures = useMemo(() => {
    let filtered = features;
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(feature =>
        feature.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (sortBy === 'recentlyAdded')
      filtered = filtered
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    else if (sortBy === 'recentlyUpdated')
      filtered = filtered
        .slice()
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.createdAt));
    else if (sortBy === 'alphabeticallyAsc')
      filtered = filtered.slice().sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'alphabeticallyDesc')
      filtered = filtered.slice().sort((a, b) => b.name.localeCompare(a.name));
    else if (sortBy === 'defaultPriceAsc')
      filtered = filtered
        .slice()
        .sort(
          (a, b) =>
            parseFloat(a.featureDefaultPrice) -
            parseFloat(b.featureDefaultPrice),
        );
    else if (sortBy === 'defaultPriceDsc')
      filtered = filtered
        .slice()
        .sort(
          (a, b) =>
            parseFloat(b.featureDefaultPrice) -
            parseFloat(a.featureDefaultPrice),
        );
    return filtered;
  }, [features, searchQuery, sortBy]);

  useEffect(() => {
    const totals = {Low: 0, Mid: 0, High: 0};
    features.forEach(feature => {
      const price =
        parseFloat(customPrices[feature.featureKey]) ||
        parseFloat(feature.featureDefaultPrice) ||
        0;
      const selection = selectedFeatures[feature.featureKey];
      if (selection) {
        if (selection.Low) totals.Low += price;
        if (selection.Mid) totals.Mid += price;
        if (selection.High) totals.High += price;
      }
    });
    const packages = getValues('packages');
    packages.forEach((pkg, index) => {
      const newMonthly = totals[pkg.packageType]?.toFixed(2);
      const newYearly = (totals[pkg.packageType] * 12)?.toFixed(2);
      if (pkg.tentativeMonthlyFeaturePrice !== newMonthly)
        setValue(`packages.${index}.tentativeMonthlyFeaturePrice`, newMonthly);
      if (pkg.tentativeYearlyFeaturePrice !== newYearly)
        setValue(`packages.${index}.tentativeYearlyFeaturePrice`, newYearly);
    });
  }, [features, customPrices, selectedFeatures, getValues, setValue]);

  useEffect(() => {
    if (watchedPackages) {
      watchedPackages.forEach((pkg, index) => {
        const finalMonthly = parseFloat(pkg.finalPackagePriceMonthly) || 0;
        const monthlyDiscount = parseFloat(pkg.monthlyDiscountValue) || 0;
        let computedMonthlyBeforeDiscount =
          pkg.monthlyDiscountType === 'percentage'
            ? finalMonthly + finalMonthly * (monthlyDiscount / 100)
            : finalMonthly + monthlyDiscount;
        const finalYearly = parseFloat(pkg.finalPackagePriceYearly) || 0;
        const yearlyDiscount = parseFloat(pkg.yearlyDiscountValue) || 0;
        let computedYearlyBeforeDiscount =
          pkg.yearlyDiscountType === 'percentage'
            ? finalYearly + finalYearly * (yearlyDiscount / 100)
            : finalYearly + yearlyDiscount;
        if (
          pkg.monthlyPriceBeforeDiscount !==
          computedMonthlyBeforeDiscount.toFixed(2)
        )
          setValue(
            `packages.${index}.monthlyPriceBeforeDiscount`,
            computedMonthlyBeforeDiscount.toFixed(2),
          );
        if (
          pkg.yearlyPriceBeforeDiscount !==
          computedYearlyBeforeDiscount.toFixed(2)
        )
          setValue(
            `packages.${index}.yearlyPriceBeforeDiscount`,
            computedYearlyBeforeDiscount.toFixed(2),
          );
      });
    }
  }, [watchedPackages, setValue]);

  const cleanAndConvertNumbers = obj => {
    if (Array.isArray(obj)) return obj.map(cleanAndConvertNumbers);
    if (obj && typeof obj === 'object') {
      const result = {};
      for (const key in obj) {
        if (
          ['countryPricing', 'countrySpecificPrices'].includes(key) &&
          obj[key] &&
          typeof obj[key] === 'object'
        ) {
          result[key] = {};
          for (const country in obj[key]) {
            const {packageType, ...rest} = obj[key][country] || {};
            result[key][country] = cleanAndConvertNumbers(rest);
          }
        } else if (
          [
            'price',
            'priceBeforeDiscount',
            'discountValue',
            'finalPrice',
          ].includes(key)
        ) {
          if (obj[key] === '' || obj[key] == null) result[key] = null;
          else {
            const num = Number(obj[key]);
            result[key] = isNaN(num) ? null : num;
          }
        } else if (typeof obj[key] === 'object')
          result[key] = cleanAndConvertNumbers(obj[key]);
        else result[key] = obj[key];
      }
      return result;
    }
    return obj;
  };

  const onSubmit = data => {
    const featurePayload = Object.entries(selectedFeatures).flatMap(
      ([featureId, pkgSelections]) => {
        const featureObj = features.find(f => f.id === featureId);
        const defaultPrice = parseFloat(featureObj?.featureDefaultPrice) || 0;
        return Object.entries(pkgSelections)
          .filter(([, isSelected]) => isSelected)
          .map(([pkgType]) => {
            const customVal = parseFloat(customPrices[featureId]);
            const finalPrice = !isNaN(customVal) ? customVal : defaultPrice;
            return {
              featureId,
              packageType: pkgType,
              price: finalPrice.toString(),
            };
          });
      },
    );

    const finalPackages = data.packages.map(pkg => ({
      packageId: pkg.packageId,
      packageType: pkg.packageType,
      packageName: pkg.packageName,
      packageDescription: pkg.packageDescription,
      active: true,
      finalMonthlyPrice: pkg.finalMonthlyPrice,
      tentativeMonthlyPrice: pkg.tentativeMonthlyPrice,
      badges: Array.isArray(pkg.badges)
        ? pkg.badges
            .filter(b => b.text && b.text.trim() !== '')
            .map(b => ({
              text: b.text,
              color: b.color || '#16a34a',
              textColor: b.textColor || '#ffffff',
            }))
        : [],
    }));

    const packageTypes = ['Low', 'Mid', 'High'];

    const buildPackagesWithDurations = countryPricing => {
      const packagesMap = packageTypes.reduce((acc, pType) => {
        acc[pType] = {packageType: pType, durations: []};
        return acc;
      }, {});
      const packageIdMap = {};
      ['low', 'mid', 'high'].forEach(pkgKey => {
        const pkg = fetchedPackages?.packages?.[pkgKey];
        if (pkg?.type && pkg?.id) packageIdMap[pkg.type] = pkg.id;
      });
      Object.entries(countryPricing).forEach(([durationStr, pricingData]) => {
        const duration = Number(durationStr);
        packageTypes.forEach(pType => {
          const countryPricingObj = {};
          if (pricingData.countryPricing) {
            for (const [countryCode, countryEntries] of Object.entries(
              pricingData.countryPricing,
            )) {
              const entryForPkg = Object.values(countryEntries).find(
                entry => entry.packageType === pType,
              );
              if (entryForPkg) countryPricingObj[countryCode] = entryForPkg;
            }
          }
          const pkgId = packageIdMap[pType];
          const defaultPkgPricing = pricingData.defaultPricing
            ? Object.values(pricingData.defaultPricing).find(
                entry => entry.packageType === pType,
              )
            : null;
          const durationObj = durations.find(d => d.value === duration);
          const durationId = durationObj ? durationObj.id : null;
          packagesMap[pType].durations.push({
            packageId: pkgId,
            durationInMonths: duration,
            durationId,
            price: defaultPkgPricing?.finalPrice ?? null,
            priceBeforeDiscount: defaultPkgPricing?.priceBeforeDiscount ?? null,
            discountType: defaultPkgPricing?.discountType ?? null,
            discountValue: defaultPkgPricing?.discountValue ?? null,
            countryPricing: countryPricingObj,
          });
        });
      });
      return Object.values(packagesMap);
    };

    const packagesWithDurations = buildPackagesWithDurations(countryPricing);
    const finalPackagesWithPricing = finalPackages.map(pkg => {
      const matchedPackage = packagesWithDurations.find(
        p => p.packageType === pkg.packageType,
      );
      return {
        ...pkg,
        durations: matchedPackage ? matchedPackage.durations : [],
      };
    });

    if (!isAcknowledged)
      return Notiflix.Notify.warning(
        'Please confirm that the details have been correctly entered.',
      );
    if (!Object.keys(selectedFeatures).length)
      return Notiflix.Notify.warning(
        'Please select features to create/update the package',
      );

    Loading.circle('Assigning features...');
    const cleanedPackages = cleanAndConvertNumbers(finalPackagesWithPricing);
    const requestBody = {
      features: featurePayload,
      additionalFeatures: selectedAdditionalFeatures,
      packages: cleanedPackages,
    };

    Axios.post(`/shop/products/${productId}/createPackage`, requestBody)
      .then(res =>
        Notify.success(
          res.data.message || 'Package created/updated successfully',
        ),
      )
      .catch(err => {
        Notify.failure(err.response?.data?.message || 'Error submitting data');
        console.error('Error', err);
      })
      .finally(() => Loading.remove('Assigning features...'));
  };

  const productColumns = [
    {Header: 'Sl.No.', accessor: 'slNo', align: 'left'},
    {Header: 'Feature', accessor: 'name', align: 'left'},
    {Header: 'Description', accessor: 'description', align: 'left'},
    {
      Header: 'Default Price',
      accessor: 'featureDefaultPrice',
      align: 'left',
      cellSx: {py: 0, color: 'primary.main'},
    },
    {
      Header: 'Custom Price',
      accessor: 'customPrice',
      align: 'left',
      padding: 0,
    },
    {Header: 'Low', accessor: 'low', align: 'center'},
    {Header: 'Mid', accessor: 'mid', align: 'center'},
    {Header: 'High', accessor: 'high', align: 'center'},
  ];

  const productRows = displayedFeatures.map((feature, idx) => {
    const featureId = feature.featureKey;
    const val = customPrices[featureId] ?? feature.featureDefaultPrice;
    const isLowGlobal = globalFeatures.Low.some(
      gf => gf.featureId === featureId,
    );
    const isMidGlobal = globalFeatures.Mid.some(
      gf => gf.featureId === featureId,
    );
    const isHighGlobal = globalFeatures.High.some(
      gf => gf.featureId === featureId,
    );
    const lowChecked =
      selectedFeatures[featureId]?.Low !== undefined
        ? selectedFeatures[featureId].Low
        : isLowGlobal;
    const midChecked =
      selectedFeatures[featureId]?.Mid !== undefined
        ? selectedFeatures[featureId].Mid
        : isMidGlobal;
    const highChecked =
      selectedFeatures[featureId]?.High !== undefined
        ? selectedFeatures[featureId].High
        : isHighGlobal;
    return {
      slNo: idx + 1,
      name: <MDTypography variant="caption">{feature.name}</MDTypography>,
      description: (
        <MDTypography variant="caption">{feature.description}</MDTypography>
      ),
      featureDefaultPrice: (
        <MDTypography variant="caption">
          ${feature.featureDefaultPrice}
        </MDTypography>
      ),
      customPrice: (
        <MDInput
          type="number"
          step="0.01"
          min="0"
          value={val}
          onChange={e => handleCustomPriceChange(featureId, e.target.value)}
          onKeyDown={e => {
            if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
          }}
        />
      ),
      low: (
        <Checkbox
          checked={lowChecked}
          onChange={() => handleCheckboxChange(featureId, 'Low')}
        />
      ),
      mid: (
        <Checkbox
          checked={midChecked}
          onChange={() => handleCheckboxChange(featureId, 'Mid')}
        />
      ),
      high: (
        <Checkbox
          checked={highChecked}
          onChange={() => handleCheckboxChange(featureId, 'High')}
        />
      ),
    };
  });

  const handleCountryChange = event => setSelectedCountry(event.target.value);
  const handleDurationChange = event => {
    setSelectedDuration(event.target.value);
    setSelectedCountry(null);
  };

  const handleAddCountryPricing = () => {
    if (!selectedDuration) {
      Notify.failure('Please select a duration first');
      return;
    }
    if (!selectedCountry) {
      Notify.failure('Please select a country first');
      return;
    }
    const selectedCountryObj = countries.find(c => c.id === selectedCountry);
    const selectedDurationObj = durations.find(d => d.id === selectedDuration);
    if (!selectedCountryObj) {
      Notify.failure('Invalid country selected');
      return;
    }
    if (!selectedDurationObj) {
      Notify.failure('Invalid duration selected');
      return;
    }
    const durationValue = selectedDurationObj.value;
    const countryCode = selectedCountryObj.countryCode;
    const newPricing = {};
    Object.keys(selectedCountryData).forEach(index => {
      newPricing[index] = {
        ...selectedCountryData[index],
        packageType: watchedPackages[index].packageType,
        duration: durationValue,
      };
    });
    setCountryPricing(prev => {
      const prevDurationData = prev[durationValue] || {};
      let duratioObj = {...prevDurationData};
      duratioObj.countryPricing = {
        ...(prevDurationData.countryPricing || {}),
        [countryCode]: {
          ...(prevDurationData.countryPricing?.[countryCode] || {}),
          ...newPricing,
        },
      };
      return {...prev, [durationValue]: duratioObj};
    });
    setActiveDurationTab(String(durationValue));
    setSelectedDuration(null);
    setSelectedCountry(null);
    setSelectedCountryData([{}, {}, {}]);
    setIsCreatePricingOpen(false);
    Notify.success(
      `Country pricing added for ${selectedCountryObj.name} (${durationValue} months)`,
    );
  };

  const handleEditPricing = (
    duration,
    pkgIndex,
    pkgData,
    type = 'default',
    countryCode = null,
  ) => {
    setEditingPricing(prev => ({
      ...prev,
      [duration]: {
        ...(prev[duration] || {}),
        [type]: {
          ...(prev[duration]?.[type] || {}),
          ...(type === 'country' && countryCode
            ? {
                [countryCode]: {
                  ...(prev[duration]?.[type]?.[countryCode] || {}),
                  [pkgIndex]: true,
                },
              }
            : {[pkgIndex]: true}),
        },
      },
    }));
    setEditValues(prev => ({
      ...prev,
      [duration]: {
        ...(prev[duration] || {}),
        [type]: {
          ...(prev[duration]?.[type] || {}),
          ...(type === 'country' && countryCode
            ? {
                [countryCode]: {
                  ...(prev[duration]?.[type]?.[countryCode] || {}),
                  [pkgIndex]: {
                    priceBeforeDiscount: pkgData.priceBeforeDiscount || '',
                    discountType: pkgData.discountType || '',
                    discountValue: pkgData.discountValue || '',
                    finalPrice: pkgData.finalPrice || '',
                  },
                },
              }
            : {
                [pkgIndex]: {
                  finalPrice: pkgData.finalPrice || pkgData.price || '',
                  priceBeforeDiscount: pkgData.priceBeforeDiscount || '',
                  discountType: pkgData.discountType || '',
                  discountValue: pkgData.discountValue || '',
                },
              }),
        },
      },
    }));
  };

  const handleEditPricingChange = (
    duration,
    pkgIndex,
    field,
    value,
    type = 'default',
    countryCode = null,
  ) => {
    setEditValues(prev => ({
      ...prev,
      [duration]: {
        ...(prev[duration] || {}),
        [type]: {
          ...(prev[duration]?.[type] || {}),
          ...(type === 'country' && countryCode
            ? {
                [countryCode]: {
                  ...(prev[duration]?.[type]?.[countryCode] || {}),
                  [pkgIndex]: {
                    ...(prev[duration]?.[type]?.[countryCode]?.[pkgIndex] ||
                      {}),
                    [field]: value,
                  },
                },
              }
            : {
                [pkgIndex]: {
                  ...(prev[duration]?.[type]?.[pkgIndex] || {}),
                  [field]: value,
                },
              }),
        },
      },
    }));
  };

  const handleSavePricing = (
    duration,
    pkgIndex,
    type = 'default',
    countryCode = null,
  ) => {
    setCountryPricing(prev => {
      const updated = {...prev};
      if (type === 'default') {
        if (updated[duration]?.defaultPricing) {
          updated[duration].defaultPricing[pkgIndex] = {
            ...updated[duration].defaultPricing[pkgIndex],
            ...editValues[duration]?.default?.[pkgIndex],
          };
        }
      } else if (type === 'country' && countryCode) {
        if (updated[duration]?.countryPricing?.[countryCode]) {
          updated[duration].countryPricing[countryCode][pkgIndex] = {
            ...updated[duration].countryPricing[countryCode][pkgIndex],
            ...editValues[duration]?.country?.[countryCode]?.[pkgIndex],
          };
        }
      }
      return updated;
    });
    setEditingPricing(prev => {
      const newState = {...prev};
      if (type === 'default') {
        if (newState[duration]?.default)
          delete newState[duration].default[pkgIndex];
      } else if (type === 'country' && countryCode) {
        if (newState[duration]?.country?.[countryCode])
          delete newState[duration].country[countryCode][pkgIndex];
      }
      return newState;
    });
  };

  const handleCancelPricingEdit = (
    duration,
    pkgIndex,
    type = 'default',
    countryCode = null,
  ) => {
    setEditingPricing(prev => {
      const newState = {...prev};
      if (type === 'default') {
        if (newState[duration]?.default)
          delete newState[duration].default[pkgIndex];
      } else if (type === 'country' && countryCode) {
        if (newState[duration]?.country?.[countryCode])
          delete newState[duration].country[countryCode][pkgIndex];
      }
      return newState;
    });
  };

  const selectedDurationObj = durations.find(d => d.id === selectedDuration);
  const selectedCountryObj = countries.find(c => c.id === selectedCountry);

  const availableCountriesForDuration = useMemo(() => {
    if (!selectedDurationObj) return [];
    const existingCountryCodes = new Set(
      Object.keys(
        countryPricing[selectedDurationObj.value]?.countryPricing || {},
      ),
    );
    return countries.filter(
      country => !existingCountryCodes.has(country.countryCode),
    );
  }, [countries, countryPricing, selectedDurationObj]);

  if (durationLoading || countryLoading) {
    return (
      <DashboardLayout>
        <MDBox
          pt={6}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px">
          <CircularProgress />
          <MDTypography ml={2}>
            Loading duration and country data...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox pt={6} mb={3} className="assignFeaturePage">
        {(durationError || countryError) && (
          <Alert severity="error" sx={{mb: 2}}>
            {durationError || countryError}
          </Alert>
        )}

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{pb: 2}}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                onClick={() => setIsFeatureTableExpanded(prev => !prev)}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}>
                <MDTypography variant="h6" color="white">
                  Product Feature Assignment
                </MDTypography>
                <MDBox
                  display="flex"
                  alignItems="center"
                  onClick={e => e.stopPropagation()}>
                  <MDInput
                    placeholder="Search by feature name"
                    value={searchQuery}
                    sx={{width: 250, marginRight: '12px'}}
                    onChange={e => setSearchQuery(e.target.value)}
                    size="small"
                  />
                  <Select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    displayEmpty
                    sx={{height: 38, width: 180, color: 'white'}}
                    inputProps={{'aria-label': 'Sort By'}}>
                    <MenuItem value="alphabeticallyAsc">
                      Alphabetically (ASC)
                    </MenuItem>
                    <MenuItem value="alphabeticallyDesc">
                      Alphabetically (DSC)
                    </MenuItem>
                    <MenuItem value="defaultPriceAsc">
                      Default Price (ASC)
                    </MenuItem>
                    <MenuItem value="defaultPriceDsc">
                      Default Price (DSC)
                    </MenuItem>
                    <MenuItem value="recentlyAdded">Recently Added</MenuItem>
                    <MenuItem value="recentlyUpdated">
                      Recently Updated
                    </MenuItem>
                  </Select>
                  <IconButton
                    size="small"
                    aria-label={
                      isFeatureTableExpanded ? 'Collapse table' : 'Expand table'
                    }
                    onClick={() => setIsFeatureTableExpanded(prev => !prev)}
                    sx={{
                      color: 'white',
                      ml: 1,
                      transform: isFeatureTableExpanded
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}>
                    <ExpandMoreIcon />
                  </IconButton>
                </MDBox>
              </MDBox>
              {isFeatureTableExpanded && (
                <MDBox pt={3}>
                  <DataTable
                    table={{columns: productColumns, rows: productRows}}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    hoverEffect
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <AdditionalFeatureAssignment
        setSelectedAdditionalFeatures={memoizedSetSelectedAdditionalFeatures}
        selectedAdditionalFeatures={selectedAdditionalFeatures}
        initialSelectedAdditionalFeatureIds={
          initialSelectedAdditionalFeatureIds
        }
        customSelectedAdditionalFeatures={customSelectedAdditionalFeatures}
        setCustomSelectedAdditionalFeatures={
          setCustomSelectedAdditionalFeatures
        }
      />

      {/* Unused: selected durations are never sent in onSubmit's requestBody
      <MDBox mt={2} p={2} sx={{backgroundColor: '#fff', borderRadius: 2}}>
        <MDTypography variant="subtitle2" sx={{fontWeight: 'bold', mb: 1}}>
          Select Durations
        </MDTypography>
        <Controller
          name="durations"
          control={control}
          render={({field: {value = [], onChange}}) => (
            <MDBox display="flex" flexWrap="wrap" gap={2} mt={1}>
              {durations.map(duration => {
                const isChecked =
                  Array.isArray(value) && value.includes(duration.id);
                return (
                  <MDBox key={duration.id} display="flex" alignItems="center">
                    <Checkbox
                      checked={isChecked}
                      onChange={() =>
                        onChange(
                          isChecked
                            ? value.filter(d => d !== duration.id)
                            : [...value, duration.id],
                        )
                      }
                    />
                    <MDTypography variant="caption">
                      {formatDurationLabel(duration)}
                      {duration.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ml: 1}}
                        />
                      )}
                    </MDTypography>
                  </MDBox>
                );
              })}
            </MDBox>
          )}
        />
      </MDBox>
      */}

      <form
        noValidate
        onSubmit={handleSubmit(onSubmit, err =>
          console.log('FORM ERRORS', err),
        )}>
        <MDBox
          mt={3}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            backgroundColor: '#fff',
            gap: 1,
            borderRadius: 2,
            padding: 2,
          }}>
          {watchedPackages &&
            watchedPackages.map((pkg, index) => {
              const imageFieldName = `${pkg?.packageType?.toLowerCase()}_pkg_image_url`;
              return (
                <MDBox
                  key={index}
                  p={2}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    width: {xs: '100%', md: 'calc((100% / 3) - 10px)'},
                  }}>
                  <MDTypography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                    }}>{`Package: ${pkg.packageType}`}</MDTypography>
                  <MDTypography variant="caption" sx={{mr: 2}}>
                    (Modify details as needed)
                  </MDTypography>
                  <MDBox mt={2}>
                    <MDTypography variant="caption">Package Name</MDTypography>
                    <Controller
                      name={`packages.${index}.packageName`}
                      control={control}
                      defaultValue={pkg.packageName || ''}
                      rules={{required: 'Package Name is required'}}
                      render={({field}) => (
                        <MDInput
                          type="text"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          fullWidth
                          sx={{mb: '8px'}}
                        />
                      )}
                    />
                    {errors.packages?.[index]?.packageName && (
                      <MDTypography
                        variant="caption"
                        color="error"
                        className="error">
                        {errors.packages[index].packageName.message}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="caption">
                      Package Description
                    </MDTypography>
                    <Controller
                      name={`packages.${index}.packageDescription`}
                      control={control}
                      defaultValue={pkg.packageDescription || ''}
                      rules={{required: 'Package Description is required'}}
                      render={({field}) => (
                        <MDInput
                          type="text"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          fullWidth
                          sx={{mb: '8px'}}
                        />
                      )}
                    />
                    {errors.packages?.[index]?.packageDescription && (
                      <MDTypography
                        variant="caption"
                        color="error"
                        className="error">
                        {errors.packages[index].packageDescription.message}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox mt={2}>
                    <MDTypography variant="caption">
                      Tags (shown on the frontend pricing card, optional)
                    </MDTypography>
                    {badgesFieldArrays[index].fields.map(
                      (badgeField, badgeIdx) => (
                        <MDBox
                          key={badgeField.id}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mt={badgeIdx === 0 ? 0.5 : 1}>
                          <Controller
                            name={`packages.${index}.badges.${badgeIdx}.text`}
                            control={control}
                            defaultValue={badgeField.text || ''}
                            render={({field}) => (
                              <MDInput
                                type="text"
                                placeholder="e.g. Most Popular"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                fullWidth
                                sx={{mb: '8px'}}
                              />
                            )}
                          />
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={0.25}>
                            <Tooltip title="Background color">
                              <Controller
                                name={`packages.${index}.badges.${badgeIdx}.color`}
                                control={control}
                                defaultValue={badgeField.color || '#16a34a'}
                                render={({field}) => (
                                  <input
                                    type="color"
                                    value={field.value || '#16a34a'}
                                    onChange={e =>
                                      field.onChange(e.target.value)
                                    }
                                    style={{
                                      width: 40,
                                      height: 40,
                                      padding: 0,
                                      border: '1px solid #ccc',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                    }}
                                  />
                                )}
                              />
                            </Tooltip>
                            <MDTypography
                              variant="caption"
                              sx={{fontSize: '9px', lineHeight: 1}}>
                              Badge
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={0.25}>
                            <Tooltip title="Font/text color">
                              <Controller
                                name={`packages.${index}.badges.${badgeIdx}.textColor`}
                                control={control}
                                defaultValue={badgeField.textColor || '#ffffff'}
                                render={({field}) => (
                                  <input
                                    type="color"
                                    value={field.value || '#ffffff'}
                                    onChange={e =>
                                      field.onChange(e.target.value)
                                    }
                                    style={{
                                      width: 40,
                                      height: 40,
                                      padding: 0,
                                      border: '1px solid #ccc',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                    }}
                                  />
                                )}
                              />
                            </Tooltip>
                            <MDTypography
                              variant="caption"
                              sx={{fontSize: '9px', lineHeight: 1}}>
                              Text
                            </MDTypography>
                          </MDBox>
                          <IconButton
                            type="button"
                            size="small"
                            aria-label="Remove tag"
                            onClick={() =>
                              badgesFieldArrays[index].remove(badgeIdx)
                            }>
                            <Delete fontSize="small" />
                          </IconButton>
                        </MDBox>
                      ),
                    )}
                    <MDButton
                      type="button"
                      variant="outlined"
                      color="info"
                      size="small"
                      sx={{mt: 1}}
                      onClick={() =>
                        badgesFieldArrays[index].append({
                          text: '',
                          color: '#16a34a',
                          textColor: '#ffffff',
                        })
                      }>
                      + Add Tag
                    </MDButton>
                  </MDBox>
                  <MDBox mt={2}>
                    <MDTypography variant="caption">Package Image</MDTypography>
                    <Controller
                      name={`packages.${index}.${imageFieldName}`}
                      control={control}
                      defaultValue={null}
                      render={({field}) => (
                        <input
                          type="file"
                          accept="image/*"
                          style={{display: 'block', marginTop: '4px'}}
                          onChange={e => field.onChange(e.target.files[0])}
                        />
                      )}
                    />
                  </MDBox>
                  <MDTypography variant="h6" sx={{fontSize: '16px', mt: 2}}>
                    Monthly Details
                  </MDTypography>
                  <MDBox mt={1}>
                    <MDTypography variant="caption">
                      Final Package Price * (Monthly):
                    </MDTypography>
                    <Controller
                      name={`packages.${index}.finalMonthlyPrice`}
                      control={control}
                      defaultValue={pkg.finalMonthlyPrice ?? ''}
                      rules={{required: 'Final Monthly Price is required'}}
                      render={({field}) => (
                        <MDInput
                          type="number"
                          inputProps={{step: '0.01', min: '0'}}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          fullWidth
                          sx={{mb: '8px'}}
                          onKeyDown={e => {
                            if (['e', 'E', '+', '-'].includes(e.key))
                              e.preventDefault();
                          }}
                        />
                      )}
                    />
                    {errors.packages?.[index]?.finalMonthlyPrice && (
                      <MDTypography
                        variant="caption"
                        color="error"
                        className="error"
                        sx={{marginTop: '-10px'}}>
                        {errors.packages[index].finalMonthlyPrice.message}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox mt={1}>
                    <MDTypography variant="caption">
                      Tentative Monthly Feature Price:{' '}
                      {pkg.tentativeMonthlyPrice || '0.00'}
                    </MDTypography>
                  </MDBox>
                  <MDBox mt={1}>
                    <MDTypography variant="caption">
                      Tentative Package Price * (Monthly):
                    </MDTypography>
                    <Controller
                      name={`packages.${index}.tentativeMonthlyPrice`}
                      control={control}
                      defaultValue={pkg.tentativeMonthlyPrice ?? ''}
                      rules={{required: 'Final Monthly Price is required'}}
                      render={({field}) => (
                        <MDInput
                          type="number"
                          inputProps={{step: '0.01', min: '0'}}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          fullWidth
                          sx={{mb: '8px'}}
                          onKeyDown={e => {
                            if (['e', 'E', '+', '-'].includes(e.key))
                              e.preventDefault();
                          }}
                        />
                      )}
                    />
                    {errors.packages?.[index]?.tentativeMonthlyPrice && (
                      <MDTypography
                        variant="caption"
                        color="error"
                        className="error"
                        sx={{marginTop: '-10px'}}>
                        {errors.packages[index].tentativeMonthlyPrice.message}
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              );
            })}
        </MDBox>

        <MDBox
          mt={3}
          sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            padding: 2,
          }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={1}>
            <MDTypography variant="h6">
              Duration & Country Specific Pricing
            </MDTypography>
            <MDButton
              type="button"
              variant="contained"
              color="info"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedDuration(null);
                setSelectedCountry(null);
                setSelectedCountryData([{}, {}, {}]);
                setIsCreatePricingOpen(true);
              }}>
              Create Pricing
            </MDButton>
          </Box>

          {Object.keys(countryPricing).length === 0 ? (
            <Alert severity="info">
              No pricing data added yet. Click &quot;Create Pricing&quot; to add
              duration & country specific pricing.
            </Alert>
          ) : (
            (() => {
              const currentDurationValue =
                activeDurationTab &&
                Object.keys(countryPricing).includes(activeDurationTab)
                  ? activeDurationTab
                  : Object.keys(countryPricing)[0];
              const durationData = countryPricing[currentDurationValue] || {};
              const countryRows = Object.entries(
                durationData.countryPricing || {},
              );
              const rawCountryFilter =
                activeCountryFilter[currentDurationValue];
              const currentCountryFilter =
                rawCountryFilter &&
                durationData.countryPricing?.[rawCountryFilter]
                  ? rawCountryFilter
                  : countryRows[0]?.[0] || '';
              const pricingType = 'country';
              const countryCodeForType = currentCountryFilter || null;
              const currencyCodeForType =
                countries
                  .find(c => c.countryCode === countryCodeForType)
                  ?.currencyCode?.toUpperCase() || null;
              const pkgSource =
                durationData.countryPricing?.[currentCountryFilter] || {};

              return (
                <MDBox>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={2}
                    mb={2}>
                    <FormControl size="small" sx={{minWidth: 180}}>
                      <InputLabel>Duration</InputLabel>
                      <Select
                        label="Duration"
                        value={currentDurationValue}
                        onChange={e => setActiveDurationTab(e.target.value)}>
                        {Object.keys(countryPricing).map(durationValue => {
                          const durationObj = durations.find(
                            d => d.value === parseInt(durationValue),
                          );
                          return (
                            <MenuItem key={durationValue} value={durationValue}>
                              {durationObj
                                ? formatDurationLabel(durationObj)
                                : `${durationValue} months`}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{minWidth: 220}}>
                      <InputLabel>Country</InputLabel>
                      <Select
                        label="Country"
                        value={currentCountryFilter}
                        onChange={e =>
                          setActiveCountryFilter(prev => ({
                            ...prev,
                            [currentDurationValue]: e.target.value,
                          }))
                        }>
                        {countryRows.map(([countryCode]) => {
                          const countryObj = countries.find(
                            c => c.countryCode === countryCode,
                          );
                          return (
                            <MenuItem key={countryCode} value={countryCode}>
                              {countryObj
                                ? `${countryObj.flagEmoji || ''} ${
                                    countryObj.name || countryObj.countryName
                                  } (${countryCode})`.trim()
                                : countryCode}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    {pricingType === 'country' && (
                      <Tooltip title="Delete this country's pricing">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            handleDeleteCountryPricing(
                              currentDurationValue,
                              countryCodeForType,
                            );
                            setActiveCountryFilter(prev => ({
                              ...prev,
                              [currentDurationValue]: '',
                            }));
                          }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {countryRows.length === 0 ? (
                    <Alert severity="info">
                      No country pricing data for this duration yet. Click
                      &quot;Create Pricing&quot; to add one.
                    </Alert>
                  ) : Object.keys(pkgSource).length === 0 ? (
                    <Alert severity="info">
                      No pricing data for this country yet.
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {['Low', 'Mid', 'High'].map(pkgType => {
                        const pkgIndex = Object.keys(pkgSource).find(
                          idx => pkgSource[idx].packageType === pkgType,
                        );
                        const pkgData =
                          pkgIndex != null ? pkgSource[pkgIndex] : null;
                        const editing =
                          pkgIndex != null &&
                          (pricingType === 'default'
                            ? editingPricing[currentDurationValue]?.default?.[
                                pkgIndex
                              ]
                            : editingPricing[currentDurationValue]?.country?.[
                                countryCodeForType
                              ]?.[pkgIndex]);
                        const editVals =
                          pkgIndex != null &&
                          (pricingType === 'default'
                            ? editValues[currentDurationValue]?.default?.[
                                pkgIndex
                              ]
                            : editValues[currentDurationValue]?.country?.[
                                countryCodeForType
                              ]?.[pkgIndex]);

                        return (
                          <Grid item xs={12} md={4} key={pkgType}>
                            <MDBox
                              sx={{
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                p: 2,
                              }}>
                              <MDTypography variant="subtitle2">
                                Package: {pkgType}
                              </MDTypography>
                              {!pkgData ? (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary">
                                  No pricing set for this package.
                                </MDTypography>
                              ) : editing ? (
                                <>
                                  <MDInput
                                    label="Price"
                                    type="number"
                                    value={editVals?.finalPrice ?? ''}
                                    onChange={e =>
                                      handleEditPricingChange(
                                        currentDurationValue,
                                        pkgIndex,
                                        'finalPrice',
                                        e.target.value,
                                        pricingType,
                                        countryCodeForType,
                                      )
                                    }
                                    fullWidth
                                    sx={{mb: 1, mt: 1}}
                                  />
                                  <MDInput
                                    label="Price Before Discount"
                                    type="number"
                                    value={editVals?.priceBeforeDiscount ?? ''}
                                    onChange={e =>
                                      handleEditPricingChange(
                                        currentDurationValue,
                                        pkgIndex,
                                        'priceBeforeDiscount',
                                        e.target.value,
                                        pricingType,
                                        countryCodeForType,
                                      )
                                    }
                                    fullWidth
                                    sx={{mb: 1}}
                                  />
                                  <Select
                                    value={editVals?.discountType ?? ''}
                                    onChange={e =>
                                      handleEditPricingChange(
                                        currentDurationValue,
                                        pkgIndex,
                                        'discountType',
                                        e.target.value,
                                        pricingType,
                                        countryCodeForType,
                                      )
                                    }
                                    fullWidth
                                    sx={{mb: 1}}>
                                    <MenuItem value="percentage">
                                      Percentage
                                    </MenuItem>
                                    <MenuItem value="flat">Flat</MenuItem>
                                  </Select>
                                  <MDInput
                                    label="Discount Value"
                                    type="number"
                                    value={editVals?.discountValue ?? ''}
                                    onChange={e =>
                                      handleEditPricingChange(
                                        currentDurationValue,
                                        pkgIndex,
                                        'discountValue',
                                        e.target.value,
                                        pricingType,
                                        countryCodeForType,
                                      )
                                    }
                                    fullWidth
                                    sx={{mb: 1}}
                                  />
                                  <Box sx={{display: 'flex', gap: 1}}>
                                    <MDButton
                                      type="button"
                                      variant="contained"
                                      color="success"
                                      size="small"
                                      onClick={() =>
                                        handleSavePricing(
                                          currentDurationValue,
                                          pkgIndex,
                                          pricingType,
                                          countryCodeForType,
                                        )
                                      }>
                                      Save
                                    </MDButton>
                                    <MDButton
                                      type="button"
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        handleCancelPricingEdit(
                                          currentDurationValue,
                                          pkgIndex,
                                          pricingType,
                                          countryCodeForType,
                                        )
                                      }>
                                      Cancel
                                    </MDButton>
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <MDTypography variant="caption">
                                    Price: {pkgData.finalPrice || '-'}
                                  </MDTypography>
                                  <br />
                                  <MDTypography variant="caption">
                                    Price Before Discount:{' '}
                                    {pkgData.priceBeforeDiscount || '-'}
                                  </MDTypography>
                                  <br />
                                  <MDTypography variant="caption">
                                    Discount Type: {pkgData.discountType || '-'}
                                  </MDTypography>
                                  <br />
                                  <MDTypography variant="caption">
                                    Discount Value:{' '}
                                    {pkgData.discountValue || '-'}
                                  </MDTypography>
                                  {pkgData.discountValue ? (
                                    <Chip
                                      size="small"
                                      label={
                                        pkgData.discountType === 'percentage'
                                          ? `${pkgData.discountValue}% off`
                                          : `${pkgData.discountValue} off`
                                      }
                                      color="success"
                                      variant="outlined"
                                      sx={{
                                        mt: 1,
                                        display: 'block',
                                        width: 'fit-content',
                                      }}
                                    />
                                  ) : null}

                                  {SUBSCRIPTION_PLAN_TYPES.some(
                                    ({key}) =>
                                      pkgData.subscriptionOptions?.[key],
                                  ) && (
                                    <MDBox
                                      sx={{
                                        mt: 1.5,
                                        pt: 1,
                                        borderTop: '1px dashed #ddd',
                                      }}>
                                      <MDTypography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 'bold',
                                          display: 'block',
                                        }}>
                                        Plan Prices
                                      </MDTypography>
                                      {SUBSCRIPTION_PLAN_TYPES.map(
                                        ({key, label}) => {
                                          const optionData =
                                            pkgData.subscriptionOptions?.[key];
                                          return (
                                            <MDTypography
                                              key={key}
                                              variant="caption"
                                              sx={{display: 'block'}}>
                                              {label}:{' '}
                                              {optionData?.finalPrice ?? '-'}
                                              {optionData?.discount ? (
                                                <MDTypography
                                                  component="span"
                                                  variant="caption"
                                                  color="success"
                                                  sx={{
                                                    ml: 0.5,
                                                    fontWeight: 'bold',
                                                  }}>
                                                  ({optionData.discount}% off)
                                                </MDTypography>
                                              ) : null}
                                            </MDTypography>
                                          );
                                        },
                                      )}
                                    </MDBox>
                                  )}

                                  {fetchedPackages?.packages?.[
                                    pkgType.toLowerCase()
                                  ]?.displayPrice?.[currencyCodeForType] !=
                                    null && (
                                    <MDBox
                                      sx={{
                                        mt: 1.5,
                                        pt: 1,
                                        borderTop: '1px dashed #ddd',
                                      }}>
                                      <MDTypography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 'bold',
                                          display: 'block',
                                        }}>
                                        Storefront Starting Price (
                                        {currencyCodeForType}):{' '}
                                        {
                                          fetchedPackages.packages[
                                            pkgType.toLowerCase()
                                          ].displayPrice[currencyCodeForType]
                                        }
                                        {pkgType === 'Low' && (
                                          <MDTypography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ml: 0.5}}>
                                            (used on best-seller card)
                                          </MDTypography>
                                        )}
                                      </MDTypography>
                                    </MDBox>
                                  )}

                                  <MDButton
                                    type="button"
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    startIcon={<EditIcon fontSize="small" />}
                                    onClick={() =>
                                      handleEditPricing(
                                        currentDurationValue,
                                        pkgIndex,
                                        pkgData,
                                        pricingType,
                                        countryCodeForType,
                                      )
                                    }
                                    sx={{mt: 1}}>
                                    Edit
                                  </MDButton>
                                </>
                              )}
                            </MDBox>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </MDBox>
              );
            })()
          )}
        </MDBox>

        <Dialog
          open={isCreatePricingOpen}
          onClose={() => {
            setIsCreatePricingOpen(false);
            setSelectedDuration(null);
            setSelectedCountry(null);
            setSelectedCountryData([{}, {}, {}]);
          }}
          maxWidth="md"
          fullWidth>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            Create Country Pricing
            <IconButton
              size="small"
              onClick={() => {
                setIsCreatePricingOpen(false);
                setSelectedDuration(null);
                setSelectedCountry(null);
                setSelectedCountryData([{}, {}, {}]);
              }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Duration</InputLabel>
                  <Select
                    value={selectedDuration || ''}
                    onChange={handleDurationChange}
                    label="Select Duration"
                    disabled={durationLoading}>
                    {durations.length === 0 ? (
                      <MenuItem disabled>
                        {durationLoading
                          ? 'Loading...'
                          : 'No durations available'}
                      </MenuItem>
                    ) : (
                      durations.map(duration => (
                        <MenuItem key={duration.id} value={duration.id}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            width="100%">
                            <Typography>
                              {formatDurationLabel(duration)}
                            </Typography>
                            {duration.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!selectedDuration}>
                  <InputLabel>Select Country</InputLabel>
                  <Select
                    value={selectedCountry || ''}
                    onChange={handleCountryChange}
                    label="Select Country"
                    disabled={countryLoading || !selectedDuration}>
                    {!selectedDuration ? (
                      <MenuItem disabled>Select a duration first</MenuItem>
                    ) : availableCountriesForDuration.length === 0 ? (
                      <MenuItem disabled>
                        {countryLoading
                          ? 'Loading...'
                          : 'Pricing already added for all countries in this duration'}
                      </MenuItem>
                    ) : (
                      availableCountriesForDuration.map(country => (
                        <MenuItem key={country.id} value={country.id}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            width="100%">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>
                                {country.flagEmoji || '🏳️'} {country.name}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              {country.isDefault && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {country.currencyCode && (
                                <Chip
                                  label={country.currencyCode}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {selectedDuration && selectedCountry && (
              <MDBox mt={3}>
                <MDTypography
                  variant="subtitle2"
                  sx={{fontWeight: 'bold', mb: 2}}>
                  Configure Pricing for:{' '}
                  {formatDurationLabel(selectedDurationObj)} in{' '}
                  {selectedCountryObj?.name}
                </MDTypography>
                <Grid container spacing={2}>
                  {watchedPackages &&
                    watchedPackages.map((pkg, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <MDBox
                          p={2}
                          sx={{
                            border: '1px solid #ccc',
                            borderRadius: 2,
                            height: '100%',
                          }}>
                          <MDTypography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 'bold',
                            }}>{`Package: ${pkg.packageType}`}</MDTypography>
                          <MDBox mt={2}>
                            <MDTypography variant="caption">Price</MDTypography>
                            <MDInput
                              type="number"
                              inputProps={{step: '0.01', min: '0'}}
                              fullWidth
                              value={
                                selectedCountryData[index]?.finalPrice || ''
                              }
                              sx={{mb: '8px'}}
                              onKeyDown={e => {
                                if (['e', 'E', '+', '-'].includes(e.key))
                                  e.preventDefault();
                              }}
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    finalPrice: e.target.value,
                                  },
                                }))
                              }
                            />
                          </MDBox>
                          <MDBox mt={2}>
                            <MDTypography variant="caption">
                              Price Before Discount
                            </MDTypography>
                            <MDInput
                              type="number"
                              inputProps={{step: '0.01', min: '0'}}
                              value={
                                selectedCountryData[index]
                                  ?.priceBeforeDiscount || ''
                              }
                              fullWidth
                              sx={{mb: '8px'}}
                              onKeyDown={e => {
                                if (['e', 'E', '+', '-'].includes(e.key))
                                  e.preventDefault();
                              }}
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    priceBeforeDiscount: e.target.value,
                                  },
                                }))
                              }
                            />
                          </MDBox>
                          <MDBox mt={1}>
                            <MDTypography variant="caption" sx={{mb: 1}}>
                              Select Discount Type
                            </MDTypography>
                            <Select
                              fullWidth
                              value={
                                selectedCountryData[index]?.discountType || ''
                              }
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    discountType: e.target.value,
                                  },
                                }))
                              }>
                              <MenuItem value="">None</MenuItem>
                              <MenuItem value="percentage">Percentage</MenuItem>
                              <MenuItem value="flat">Flat</MenuItem>
                            </Select>
                          </MDBox>
                          <MDBox mt={1}>
                            <MDTypography variant="caption">
                              Discount Value
                            </MDTypography>
                            <MDInput
                              type="number"
                              inputProps={{step: '0.01', min: '0'}}
                              value={
                                selectedCountryData[index]?.discountValue || ''
                              }
                              fullWidth
                              sx={{mb: '8px'}}
                              onKeyDown={e => {
                                if (['e', 'E', '+', '-'].includes(e.key))
                                  e.preventDefault();
                              }}
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    discountValue: e.target.value,
                                  },
                                }))
                              }
                            />
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                </Grid>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setIsCreatePricingOpen(false);
                setSelectedDuration(null);
                setSelectedCountry(null);
                setSelectedCountryData([{}, {}, {}]);
              }}>
              Cancel
            </MDButton>
            <MDButton
              type="button"
              variant="contained"
              color="success"
              onClick={handleAddCountryPricing}
              disabled={!selectedDuration || !selectedCountry}>
              Add Country Pricing
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isCreatePricingOpen}
          onClose={() => {
            setIsCreatePricingOpen(false);
            setSelectedDuration(null);
            setSelectedCountry(null);
            setSelectedCountryData([{}, {}, {}]);
          }}
          maxWidth="md"
          fullWidth>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            Create Country Pricing
            <IconButton
              size="small"
              onClick={() => {
                setIsCreatePricingOpen(false);
                setSelectedDuration(null);
                setSelectedCountry(null);
                setSelectedCountryData([{}, {}, {}]);
              }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Duration</InputLabel>
                  <Select
                    value={selectedDuration || ''}
                    onChange={handleDurationChange}
                    label="Select Duration"
                    disabled={durationLoading}>
                    {durations.length === 0 ? (
                      <MenuItem disabled>
                        {durationLoading
                          ? 'Loading...'
                          : 'No durations available'}
                      </MenuItem>
                    ) : (
                      durations.map(duration => (
                        <MenuItem key={duration.id} value={duration.id}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            width="100%">
                            <Typography>
                              {formatDurationLabel(duration)}
                            </Typography>
                            {duration.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!selectedDuration}>
                  <InputLabel>Select Country</InputLabel>
                  <Select
                    value={selectedCountry || ''}
                    onChange={handleCountryChange}
                    label="Select Country"
                    disabled={countryLoading || !selectedDuration}>
                    {!selectedDuration ? (
                      <MenuItem disabled>Select a duration first</MenuItem>
                    ) : availableCountriesForDuration.length === 0 ? (
                      <MenuItem disabled>
                        {countryLoading
                          ? 'Loading...'
                          : 'Pricing already added for all countries in this duration'}
                      </MenuItem>
                    ) : (
                      availableCountriesForDuration.map(country => (
                        <MenuItem key={country.id} value={country.id}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            width="100%">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>
                                {country.flagEmoji || '🏳️'} {country.name}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              {country.isDefault && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {country.currencyCode && (
                                <Chip
                                  label={country.currencyCode}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {selectedDuration && selectedCountry && (
              <MDBox mt={3}>
                <MDTypography
                  variant="subtitle2"
                  sx={{fontWeight: 'bold', mb: 2}}>
                  Configure Pricing for:{' '}
                  {formatDurationLabel(selectedDurationObj)} in{' '}
                  {selectedCountryObj?.name}
                </MDTypography>
                <Grid container spacing={2}>
                  {watchedPackages &&
                    watchedPackages.map((pkg, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <MDBox
                          p={2}
                          sx={{
                            border: '1px solid #ccc',
                            borderRadius: 2,
                            height: '100%',
                          }}>
                          <MDTypography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 'bold',
                            }}>{`Package: ${pkg.packageType}`}</MDTypography>
                          <MDBox mt={2}>
                            <MDTypography variant="caption">Price</MDTypography>
                            <MDInput
                              type="number"
                              inputProps={{step: '0.01', min: '0'}}
                              fullWidth
                              value={
                                selectedCountryData[index]?.finalPrice || ''
                              }
                              sx={{mb: '8px'}}
                              onKeyDown={e => {
                                if (['e', 'E', '+', '-'].includes(e.key))
                                  e.preventDefault();
                              }}
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    finalPrice: e.target.value,
                                  },
                                }))
                              }
                            />
                          </MDBox>
                          <MDBox mt={2}>
                            <MDTypography variant="caption">
                              Price Before Discount
                            </MDTypography>
                            <MDInput
                              type="number"
                              inputProps={{step: '0.01', min: '0'}}
                              value={
                                selectedCountryData[index]
                                  ?.priceBeforeDiscount || ''
                              }
                              fullWidth
                              sx={{mb: '8px'}}
                              onKeyDown={e => {
                                if (['e', 'E', '+', '-'].includes(e.key))
                                  e.preventDefault();
                              }}
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    priceBeforeDiscount: e.target.value,
                                  },
                                }))
                              }
                            />
                          </MDBox>
                          <MDBox mt={1}>
                            <MDTypography variant="caption" sx={{mb: 1}}>
                              Select Discount Type
                            </MDTypography>
                            <Select
                              fullWidth
                              value={
                                selectedCountryData[index]?.discountType || ''
                              }
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    discountType: e.target.value,
                                  },
                                }))
                              }>
                              <MenuItem value="">None</MenuItem>
                              <MenuItem value="percentage">Percentage</MenuItem>
                              <MenuItem value="flat">Flat</MenuItem>
                            </Select>
                          </MDBox>
                          <MDBox mt={1}>
                            <MDTypography variant="caption">
                              Discount Value
                            </MDTypography>
                            <MDInput
                              type="number"
                              inputProps={{step: '0.01', min: '0'}}
                              value={
                                selectedCountryData[index]?.discountValue || ''
                              }
                              fullWidth
                              sx={{mb: '8px'}}
                              onKeyDown={e => {
                                if (['e', 'E', '+', '-'].includes(e.key))
                                  e.preventDefault();
                              }}
                              onChange={e =>
                                setSelectedCountryData(prev => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    discountValue: e.target.value,
                                  },
                                }))
                              }
                            />
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                </Grid>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setIsCreatePricingOpen(false);
                setSelectedDuration(null);
                setSelectedCountry(null);
                setSelectedCountryData([{}, {}, {}]);
              }}>
              Cancel
            </MDButton>
            <MDButton
              type="button"
              variant="contained"
              color="success"
              onClick={handleAddCountryPricing}
              disabled={!selectedDuration || !selectedCountry}>
              Add Country Pricing
            </MDButton>
          </DialogActions>
        </Dialog>

        <MDTypography
          variant="h6"
          color="warning"
          sx={{fontSize: '16px', mt: 2, mb: 1}}>
          N.B.: Final Package Price will be what is entered in the Final Package
          Price input box! (Monthly/Yearly)*
        </MDTypography>
        <MDBox
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
          <Checkbox
            sx={{border: '0.5px solid blue'}}
            onChange={e => setIsAcknowledged(e.target.checked)}
            checked={isAcknowledged}
          />
          <MDTypography
            variant="body2"
            sx={{cursor: 'pointer', userSelect: 'none'}}
            onClick={() => setIsAcknowledged(!isAcknowledged)}>
            I hereby confirm that I have accurately filled out the package
            details.
          </MDTypography>
        </MDBox>
        <MDButton
          variant="contained"
          color="info"
          type="submit"
          sx={{mt: 3, width: '200px'}}>
          Submit
        </MDButton>
      </form>
    </DashboardLayout>
  );
}
